// ProtectedRoute.tsx
import React, { useCallback, useEffect, useState } from "react";
import { checkSession, redirectOnUnauthorized } from "./api/client";
import "./ProtectedRoute.scss";

const MOBILE_APP_URL = "https://mobile2.chio-chio.ru/";

const AuthorizationLoadingScreen: React.FC<{ label?: string }> = ({ label = "Загрузка" }) => (
    <main className="lkfr-auth-loading" aria-busy="true" aria-live="polite">
        <section className="lkfr-auth-loading__card" aria-label={label}>
            <div className="lkfr-auth-loading__mark" aria-hidden="true">
                <span />
                <span />
                <span />
            </div>
            <div className="lkfr-auth-loading__title">{label}</div>
            <div className="lkfr-auth-loading__lines" aria-hidden="true">
                <span />
                <span />
                <span />
            </div>
        </section>
    </main>
);

const AuthorizationErrorScreen: React.FC = () => (
    <main className="lkfr-auth-error">
        <section className="lkfr-auth-error__content" aria-labelledby="lkfr-auth-error-title">
            <div className="lkfr-auth-error__mark" aria-hidden="true">!</div>
            <h1 id="lkfr-auth-error-title" className="lkfr-auth-error__title">
                Ошибка авторизации
            </h1>
            <p className="lkfr-auth-error__text">
                У вас нет доступа к приложению франчайзи. Поставьте роль владельца в crm системе для каждой точки.
            </p>
            <a className="lkfr-auth-error__button" href={MOBILE_APP_URL}>
                Открыть приложение
            </a>
        </section>
    </main>
);

const ConnectionErrorScreen: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
    <main className="lkfr-auth-error">
        <section className="lkfr-auth-error__content" aria-labelledby="lkfr-connection-error-title">
            <div className="lkfr-auth-error__mark" aria-hidden="true">!</div>
            <h1 id="lkfr-connection-error-title" className="lkfr-auth-error__title">
                Не удалось связаться с сервисом
            </h1>
            <p className="lkfr-auth-error__text">
                Проверьте подключение к интернету и повторите попытку. Если связь есть, сервис может быть временно недоступен.
            </p>
            <button className="lkfr-auth-error__button" type="button" onClick={onRetry}>
                Повторить
            </button>
        </section>
    </main>
);

export const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [authorized, setAuthorized] = useState<boolean | null>(null);
    const [authError, setAuthError] = useState<"unavailable" | "forbidden" | null>(null);
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => {
        setLoading(true);
        setAuthError(null);
        setAttempt((current) => current + 1);
    }, []);

    useEffect(() => {
        if (loading || authError !== "unavailable") return;
        const onNativeSession = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            if (detail?.hasAccessToken || detail?.hasRefreshToken) retry();
        };
        window.addEventListener("online", retry);
        window.addEventListener("cc_native_session_bootstrap", onNativeSession);
        return () => {
            window.removeEventListener("online", retry);
            window.removeEventListener("cc_native_session_bootstrap", onNativeSession);
        };
    }, [authError, loading, retry]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const authorized = await checkSession();
                if (active) {
                    setAuthorized(authorized);
                    setAuthError(null);
                }
            } catch (error) {
                if (active) {
                    setAuthorized(null);
                    setAuthError(error instanceof Error && error.message === "HTTP 403" ? "forbidden" : "unavailable");
                }
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [attempt]);

    useEffect(() => {
        if (!loading && authorized === false) {
            redirectOnUnauthorized();
        }
    }, [loading, authorized]);

    if (loading) return <AuthorizationLoadingScreen />;

    if (authorized === false) {
        return <AuthorizationLoadingScreen label="Перенаправление" />;
    }

    if (authError === "unavailable") {
        return <ConnectionErrorScreen onRetry={retry} />;
    }

    if (authError === "forbidden") {
        return <AuthorizationErrorScreen />;
    }

    return children;
};
